<script setup lang="ts">
/**
 * TheSidebar - side navigation.
 * Responsive: full-size on desktop, drawer on mobile.
 */
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'

const props = defineProps<{
  collapsed?: boolean
}>()

defineEmits<{ toggle: [] }>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()

interface NavItem {
  key: string
  label: string
  path: string
  icon: string
}

const navItems = computed<NavItem[]>(() => [
  {
    key: 'dashboard',
    label: t('nav.dashboard'),
    path: '/dashboard',
    icon: 'dashboard',
  },
  {
    key: 'bots',
    label: t('nav.bots'),
    path: '/bots',
    icon: 'bots',
  },
  {
    key: 'apiKeys',
    label: t('nav.apiKeys'),
    path: '/settings/api-keys',
    icon: 'key',
  },
  {
    key: 'security',
    label: t('nav.security'),
    path: '/settings/security',
    icon: 'shield',
  },
])

function isActive(path: string): boolean {
  return route.path === path || route.path.startsWith(path + '/')
}

function navigate(path: string) {
  router.push(path)
}

function logout() {
  authStore.logout()
}
</script>

<template>
  <aside
    class="flex flex-col h-full bg-base-200 border-r border-primary/5 transition-all duration-300"
    :class="collapsed ? 'w-16' : 'w-60'"
  >
    <div class="flex items-center h-16 px-4 border-b border-primary/5">
      <button
        class="flex items-center gap-2 group"
        @click="$emit('toggle')"
      >
        <img
          src="/logo_altermex.png"
          alt="AlterMex"
          class="w-8 h-8 shrink-0"
        >
        <span
          v-if="!collapsed"
          class="text-brand text-lg transition-opacity"
        >
          AlterMex
        </span>
      </button>
    </div>

    <nav class="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
      <button
        v-for="item in navItems"
        :key="item.key"
        class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
        :class="
          isActive(item.path)
            ? 'bg-primary/10 text-primary'
            : 'text-base-content/60 hover:text-base-content hover:bg-base-300/50'
        "
        @click="navigate(item.path)"
      >
        <svg
          v-if="item.icon === 'dashboard'"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-5 h-5 shrink-0"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z"
          />
        </svg>

        <svg
          v-else-if="item.icon === 'bots'"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-5 h-5 shrink-0"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 6V3m0 3a4.5 4.5 0 0 0-4.5 4.5v4.125A2.625 2.625 0 0 0 10.125 17.25h3.75A2.625 2.625 0 0 0 16.5 14.625V10.5A4.5 4.5 0 0 0 12 6Zm-6.75 5.25h1.5m10.5 0h1.5M9 20.25h6"
          />
        </svg>

        <svg
          v-else-if="item.icon === 'key'"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-5 h-5 shrink-0"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z"
          />
        </svg>

        <svg
          v-else-if="item.icon === 'shield'"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-5 h-5 shrink-0"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
          />
        </svg>

        <span
          v-if="!collapsed"
          class="truncate"
        >{{ item.label }}</span>
      </button>
    </nav>

    <div class="border-t border-primary/5 p-2">
      <div
        class="flex items-center gap-3 px-3 py-2 rounded-lg"
        :class="collapsed ? 'justify-center' : ''"
      >
        <div class="avatar placeholder">
          <div class="bg-primary/10 text-primary rounded-full w-8 h-8">
            <span class="text-xs font-bold">
              {{ authStore.displayName?.charAt(0)?.toUpperCase() ?? 'U' }}
            </span>
          </div>
        </div>
        <div
          v-if="!props.collapsed"
          class="flex-1 min-w-0"
        >
          <p class="text-sm font-medium truncate">
            {{ authStore.displayName }}
          </p>
          <button
            class="text-xs text-base-content/40 hover:text-error transition-colors"
            @click="logout"
          >
            {{ t('auth.logout') }}
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>
